// IndexedDB-backed queue for offline API mutations.
// Uses the `idb` library. Queues POST/PUT/DELETE requests when offline
// and replays them (in insertion order) once connectivity is restored.
import { openDB } from "idb";

const DB_NAME = "the-first-offline";
const DB_VERSION = 2;
const STORE_QUEUE = "mutation_queue";
const STORE_CACHE = "api_cache";

let dbPromise = null;

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (!db.objectStoreNames.contains(STORE_QUEUE)) {
          db.createObjectStore(STORE_QUEUE, { keyPath: "id", autoIncrement: true });
        }
        if (!db.objectStoreNames.contains(STORE_CACHE)) {
          db.createObjectStore(STORE_CACHE, { keyPath: "key" });
        }
        // v2: no-op (schema already fine); reserved for future migrations
        void oldVersion;
      },
    });
  }
  return dbPromise;
}

/** Enqueue a pending mutation. Returns the queued id. */
export async function enqueueMutation(entry) {
  const db = await getDB();
  const record = {
    method: entry.method,
    url: entry.url,
    data: entry.data ?? null,
    headers: entry.headers ?? {},
    createdAt: Date.now(),
  };
  const id = await db.add(STORE_QUEUE, record);
  window.dispatchEvent(new CustomEvent("offline-queue-changed"));
  return id;
}

/** Return all queued mutations (in insertion order). */
export async function listQueued() {
  const db = await getDB();
  return db.getAll(STORE_QUEUE);
}

export async function countQueued() {
  const db = await getDB();
  return db.count(STORE_QUEUE);
}

export async function removeQueued(id) {
  const db = await getDB();
  await db.delete(STORE_QUEUE, id);
  window.dispatchEvent(new CustomEvent("offline-queue-changed"));
}

/** Cache a successful GET response body (keyed by full URL). */
export async function cacheGet(key, data) {
  const db = await getDB();
  await db.put(STORE_CACHE, { key, data, ts: Date.now() });
}

export async function readCache(key) {
  const db = await getDB();
  const record = await db.get(STORE_CACHE, key);
  return record ? record.data : null;
}

/** Replay all queued mutations sequentially using the given axios client. */
export async function flushQueue(client) {
  const queued = await listQueued();
  if (!queued.length) return { flushed: 0, failed: 0 };
  let flushed = 0;
  let failed = 0;
  for (const entry of queued) {
    try {
      await client.request({
        method: entry.method,
        url: entry.url,
        data: entry.data,
        headers: { ...(entry.headers || {}), "X-Offline-Replay": "1" },
      });
      await removeQueued(entry.id);
      flushed += 1;
    } catch (err) {
      // If the server rejected the request (4xx), drop it: retrying won't help.
      const status = err?.response?.status;
      if (status && status >= 400 && status < 500) {
        await removeQueued(entry.id);
        failed += 1;
      } else {
        // Network still down or server 5xx: stop and try later.
        break;
      }
    }
  }
  return { flushed, failed };
}
